import { useCallback, useMemo, useState } from "react";
import { useSelector } from "react-redux";

import {
  useBoardUsers,
  useGetBoardUser,
} from "../../../../../../reactQuery/users";
import { useCustomSearchParams } from "../../../../../../hooks/useCustomSearchParams";
import useBoardIdByKey from "../../../../../../hooks/useBoardIdByKey";
import { getResponseData } from "../../../../../../reactQuery/response";
import { isAdmin } from "../../../../../../utils/constants";
import { normalizeBoardHeaderData } from "../../hooks/boardHeaderUtils";

const byLastSeenDesc = (userA, userB) =>
  new Date(userB?.last_seen || 0) - new Date(userA?.last_seen || 0);

export function useBoardUsersState({
  boardName,
  boardId,
  ownerId,
  isColabrotar = false,
  cardCollabrator,
}) {
  const { id } = useBoardIdByKey();
  const boardQueryId = id || boardId;
  const [anchorEl, setAnchorEl] = useState(null);
  const [openEditUserDialog, setOpenEditUserDialog] = useState(null);
  const { data: boardUsersQuery } = useBoardUsers(boardQueryId);
  const { data: boardUserQuery } = useGetBoardUser(id, openEditUserDialog);
  const [selectedUserSearch, updateSearchParams, removeSearchParam] =
    useCustomSearchParams("search");
  const currentUser = useSelector((state) => state.users.logInUser);

  const board = useMemo(() => normalizeBoardHeaderData(boardName), [boardName]);
  const boardUsers = useMemo(
    () => getResponseData(boardUsersQuery, []),
    [boardUsersQuery]
  );
  const users = useMemo(
    () => (isColabrotar ? cardCollabrator || [] : boardUsers),
    [boardUsers, cardCollabrator, isColabrotar]
  );
  const sortedUsers = useMemo(
    () => (isColabrotar ? users : [...users].sort(byLastSeenDesc)),
    [isColabrotar, users]
  );
  const selectedUsers = useMemo(
    () => selectedUserSearch?.split(",").filter(Boolean) || [],
    [selectedUserSearch]
  );
  const currentBoardUser = useMemo(
    () => getResponseData(boardUserQuery, null),
    [boardUserQuery]
  );
  const canManageUsers = useMemo(
    () =>
      isAdmin() ||
      ownerId == currentUser?.user_id ||
      board?.board_permission?.is_admin,
    [board, currentUser?.user_id, ownerId]
  );

  const closeUserList = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleAnchorClick = useCallback((event) => {
    setAnchorEl(event?.currentTarget);
  }, []);

  const handleCloseEditDialog = useCallback(() => {
    setOpenEditUserDialog(null);
  }, []);

  const filterUserById = useCallback(
    (userId) => {
      if (isColabrotar) {
        return;
      }

      const nextId = String(userId);
      const nextSelectedUsers = selectedUsers.includes(nextId)
        ? selectedUsers.filter((selectedId) => selectedId !== nextId)
        : [...selectedUsers, nextId];

      if (!nextSelectedUsers.length) {
        removeSearchParam("search");
        return;
      }

      updateSearchParams("search", nextSelectedUsers.join(","));
    },
    [isColabrotar, removeSearchParam, selectedUsers, updateSearchParams]
  );

  const removeSelectedUser = useCallback(() => {
    removeSearchParam("search");
  }, [removeSearchParam]);

  return {
    anchorEl,
    board,
    boardOwnerId: board?.owner_user_id,
    boardUsers,
    canManageUsers,
    closeUserList,
    currentBoardUser,
    filterUserById,
    handleAnchorClick,
    handleCloseEditDialog,
    open: Boolean(anchorEl),
    openEditUserDialog,
    removeSelectedUser,
    selectedUsers,
    setOpenEditUserDialog,
    sortedUsers,
    users,
  };
}
